import { Injectable, signal } from '@angular/core';
import { Router, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { selectSelectedFormation } from '../store/selectors/formation.selectors';
import { AuthenticationService } from '../service/authentification.service';
import { StudentService } from '../service/student.service';
import { FormationService } from '../service/formation.service';
import * as formationActions from '../store/actions/formation.actions';

/**
 * Guard pour vérifier si l'utilisateur a toutes les informations requises
 * Pattern inspiré de ProfileStoreGuard avec appels API directs
 * 
 * Redirige vers /missing-information si :
 * - Pas de photo profil
 * - OU informations manquantes (birthdate, gender, job/business)
 */
@Injectable({
  providedIn: 'root'
})
export class MissingInformationGuard implements CanActivateChild {
  // Signal pour stocker les données localement (pattern ProfileStoreGuard)
  private studentSignal = signal<any>(null);
  private dataLoaded = false;
  
  constructor(
    private store: Store,
    private router: Router,
    private authService: AuthenticationService,
    private studentService: StudentService,
    private formationService: FormationService
  ) { }

  // Méthode pour forcer le rechargement des données
  public resetUserDataLoaded(): void {
    this.dataLoaded = false;
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Exclure la route /missing-information pour éviter la boucle infinie
    if (state.url.includes('/missing-information')) {
      return true;
    }

    return this.checkUserInformation();
  }

  /**
   * Charge les données utilisateur via API
   */
  private loadData(): Observable<any> {
    return this.authService.me().pipe(
      map((student: any) => {
        this.studentSignal.set(student);
        this.dataLoaded = true;
        return student;
      })
    );
  }

  /**
   * Vérifie si l'utilisateur a une photo de profil
   */
  private needsPhotoProfile(user: any): boolean {
    return !user.photo_profile;
  }

  /**
   * Vérifie si les informations professionnelles sont complètes
   */
  private hasCompleteJobInfo(user: any): boolean {
    const isRetiredOrStudent = user.job_situation === 'retired' || user.job_situation === 'student';
    return user.job_situation != null && (
      (user.business && user.job) || isRetiredOrStudent
    );
  }

  /**
   * Vérifie si l'utilisateur a toutes les informations requises
   */
  private hasMissingInformation(user: any): boolean {
    const needsPhoto = this.needsPhotoProfile(user);
    const needsBirthdate = !user.birthdate;
    const needsGender = !user.gender;
    const needsJobInfo = !this.hasCompleteJobInfo(user);

    return needsPhoto || needsBirthdate || needsGender || needsJobInfo;
  }

  /**
   * Vérifie les informations utilisateur et redirige si nécessaire
   */
  private checkUserInformation(): Observable<boolean> {
    if (!this.dataLoaded) {
      return this.loadData().pipe(
        map((student) => this.validateUserInformation(student))
      );
    }
    
    const student = this.studentSignal();
    return of(this.validateUserInformation(student));
  }

  /**
   * Valide les informations utilisateur et redirige si nécessaire
   */
  private validateUserInformation(student: any): boolean {
    const user = student?.user;
    const formation = student?.formations?.[0];
    
    if (!user) {
      return true;
    }

    if (formation && formation.status === 'suspended') {
      this.router.navigateByUrl('/suspended');
      return false;
    }

    if (formation && formation.status === 'pending') {
      return true;
    }

    if (this.hasMissingInformation(user)) {
      this.router.navigateByUrl('/missing-information');
      return false;
    }

    return true;
  }
}
